import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  FileText, CheckCircle2, XCircle, Loader2, Calendar, DollarSign,
  Package, User, AlertCircle, HardHat
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicQuoteView() {
  const { shareToken } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRespond, setShowRespond] = useState(false);
  const [responseType, setResponseType] = useState('');
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const res = await fetch(`${API}/quotes/view/${shareToken}`);
        if (res.ok) {
          setQuote(await res.json());
        } else if (res.status === 404) {
          setError('Quote not found or link has expired.');
        } else {
          setError('Failed to load quote.');
        }
      } catch (e) {
        setError('Failed to connect to server.');
      } finally {
        setLoading(false);
      }
    };
    if (shareToken) fetchQuote();
  }, [shareToken]);

  const respondToQuote = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/quotes/view/${shareToken}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: responseType, feedback })
      });
      if (res.ok) {
        setSubmitted(true);
        setShowRespond(false);
        setQuote(prev => ({ ...prev, status: responseType === 'accept' ? 'accepted' : 'rejected' }));
      }
    } catch (e) {}
    finally { setSubmitting(false); }
  };

  const fmtCurrency = (v) => v != null ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7FAFB] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1D4053] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F7FAFB] flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-[#E2E8ED] shadow-lg">
          <CardContent className="pt-8 pb-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-[#0D1830] mb-2">Quote Not Found</h2>
            <p className="text-sm text-[#446E87]">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isResponded = ['accepted', 'rejected'].includes(quote?.status);

  return (
    <div className="min-h-screen bg-[#F7FAFB]" data-testid="public-quote-view">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8ED]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gold-gradient flex items-center justify-center">
              <img src="/nivela-logo.png" alt="Nivela" className="h-7" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#0D1830]">Nivela</h1>
              <p className="text-xs text-[#72A4BF]">Construction Takeoff Quote</p>
            </div>
          </div>
          {quote?.version > 1 && (
            <Badge className="bg-[#72A4BF]/10 text-[#0D1830] border-0">Revision {quote.version}</Badge>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Quote Header */}
        <Card className="border-[#E2E8ED] shadow-sm mb-6">
          <CardHeader className="pb-4 bg-gradient-to-r from-[#72A4BF]/10 to-white">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-[#0D1830] font-medium uppercase tracking-wide mb-1">Quote</p>
                <CardTitle className="text-xl">{quote?.quote_name || 'Untitled Quote'}</CardTitle>
                {quote?.customer_name && (
                  <p className="text-sm text-[#446E87] mt-1 flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />{quote.customer_name}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-xs text-[#72A4BF] mb-1">Total Price</p>
                <p className="text-3xl font-bold gold-text">{fmtCurrency(quote?.total_price)}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2 text-[#446E87]">
                <Package className="w-4 h-4" />
                <span>{quote?.items?.length || 0} items</span>
              </div>
              {quote?.valid_until && (
                <div className="flex items-center gap-2 text-[#446E87]">
                  <Calendar className="w-4 h-4" />
                  <span>Valid until {new Date(quote.valid_until).toLocaleDateString()}</span>
                </div>
              )}
              {quote?.status && (
                <Badge className={`${
                  quote.status === 'accepted' ? 'bg-green-100 text-green-700' :
                  quote.status === 'rejected' ? 'bg-red-100 text-red-700' :
                  'bg-blue-100 text-blue-700'
                } border-0 text-xs`}>
                  {quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Items Table */}
        {quote?.items?.length > 0 && (
          <Card className="border-[#E2E8ED] shadow-sm mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#0D1830]" />
                Quote Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8ED]">
                    <TableHead className="text-xs">Item</TableHead>
                    <TableHead className="text-xs text-right">Qty</TableHead>
                    <TableHead className="text-xs text-right">Unit Price</TableHead>
                    <TableHead className="text-xs text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quote.items.map((item, i) => (
                    <TableRow key={i} className="border-[#F0F4F7]">
                      <TableCell className="text-sm font-medium">{item.component_name || item.description || item.name || item.item_name}</TableCell>
                      <TableCell className="text-sm text-right">{item.quantity} {item.unit}</TableCell>
                      <TableCell className="text-sm text-right">{fmtCurrency(item.price ?? item.unit_price)}</TableCell>
                      <TableCell className="text-sm text-right font-medium">{fmtCurrency(item.total_price ?? (item.price * item.quantity))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex justify-end mt-4 pt-4 border-t border-[#E2E8ED]">
                <div className="text-right">
                  <p className="text-xs text-[#72A4BF]">Grand Total</p>
                  <p className="text-2xl font-bold gold-text">{fmtCurrency(quote.total_price)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Terms & Conditions */}
        {quote?.terms_conditions && (
          <Card className="border-[#E2E8ED] shadow-sm mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Terms & Conditions</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#446E87] whitespace-pre-wrap">{quote.terms_conditions}</p>
            </CardContent>
          </Card>
        )}

        {/* Response Section */}
        {!isResponded && !submitted ? (
          <Card className="border-[#1D4053]/20 shadow-sm bg-[#72A4BF]/10">
            <CardContent className="py-8 text-center">
              <h3 className="text-lg font-semibold text-[#0D1830] mb-2">Ready to Respond?</h3>
              <p className="text-sm text-[#446E87] mb-6">Review the quote above and let us know your decision.</p>
              <div className="flex items-center justify-center gap-4">
                <Button
                  onClick={() => { setResponseType('accept'); setShowRespond(true); }}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8"
                  data-testid="accept-quote-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Accept Quote
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setResponseType('reject'); setShowRespond(true); }}
                  className="border-red-300 text-red-600 hover:bg-red-50 rounded-full px-8"
                  data-testid="reject-quote-btn"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Decline
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className={`border-0 shadow-sm ${quote?.status === 'accepted' ? 'bg-emerald-50' : 'bg-red-50'}`}>
            <CardContent className="py-8 text-center">
              {quote?.status === 'accepted' ? (
                <>
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-emerald-700">Quote Accepted!</h3>
                  <p className="text-sm text-emerald-600 mt-1">Thank you for your response. We'll be in touch shortly.</p>
                </>
              ) : (
                <>
                  <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-red-700">Quote Declined</h3>
                  <p className="text-sm text-red-600 mt-1">Thank you for letting us know.</p>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E2E8ED] bg-white py-6 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs text-[#72A4BF]/60">Powered by Nivela • AI Construction Takeoffs</p>
        </div>
      </footer>

      {/* Response Dialog */}
      <Dialog open={showRespond} onOpenChange={setShowRespond}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {responseType === 'accept' ? (
                <><CheckCircle2 className="w-5 h-5 text-emerald-500" />Accept Quote</>
              ) : (
                <><XCircle className="w-5 h-5 text-red-500" />Decline Quote</>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-[#0D1830] mb-2 block">
              Feedback (optional)
            </label>
            <Textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={responseType === 'accept' 
                ? "Any additional notes or requirements..." 
                : "Let us know why you're declining..."}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRespond(false)} className="rounded-full">
              Cancel
            </Button>
            <Button
              onClick={respondToQuote}
              disabled={submitting}
              className={`rounded-full ${
                responseType === 'accept' 
                  ? 'bg-emerald-500 hover:bg-emerald-600 text-white' 
                  : 'bg-red-500 hover:bg-red-600 text-white'
              }`}
              data-testid="confirm-response-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {responseType === 'accept' ? 'Confirm Accept' : 'Confirm Decline'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
